import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, Divider, FormControl, FormControlLabel, Grid, IconButton, Paper, TextField, Typography, Box, Chip } from "@mui/material";
import React, { useMemo, useState } from "react";

import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import {
  useDeleteChannelMutation,
  useGetChannelsQuery,
  useUpdateChannelMutation,
} from "../../services/channel.query";
import { DefaultPage } from "../../layouts/default-page";
import { Add, Delete, Edit, Tv, Close } from "@mui/icons-material";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../../components/common/StatusChip";
import { RowActions } from "../../components/common/RowActions";
import { MRT_ColumnDef } from "material-react-table";
import DeleteConfirmationDialog from "../../layouts/delete-confirm";
import { format } from "date-fns";
import * as Yup from "yup";
import { Notify } from "../../layouts/notify";
import { LoadingButton } from "@mui/lab";

const validationSchema = Yup.object({
  is_app_displayed: Yup.boolean().required("Status is required"),
});

export default function ChannelList() {
  const {
    data: channels,
    isLoading,
    isError,
    isSuccess,
    isFetching,
  } = useGetChannelsQuery("");
  const [selectedItem, setSelectedItem] = React.useState<any>(null);
  const [modalState, setModalState] = useState<boolean>(false);
  const [updateChannel, { isLoading: updateChannelLoading }] = useUpdateChannelMutation();
  const [editMode, setEditMode] = useState<boolean>(false);

  // Layout UI State
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [draftSearch, setDraftSearch] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");

  const handleCloseModal = () => {
    setModalState(false);
  };

  const openAddEditModal = (channel: any) => {
    if (channel) {
      setEditMode(true); // Set edit mode when opening existing committee
      setSelectedItem(channel);
      formik.setValues({
        is_app_displayed: channel.is_app_displayed, // Set is_active state
      });
    } else {
      formik.resetForm();
      setEditMode(false); // Reset to add mode when opening new committee
    }
    setModalState(true);
  };

  const formik = useFormik({
    initialValues: {
      is_app_displayed: true, // Initialize is_active state
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        if (editMode) {
          // Handle updating committee
          await updateChannel({
            ...values,
            id: selectedItem.id, // Assuming you have the id of the committee
          });
          Notify("info", "Channel updated successfully");
        } else {
          // Add channel logic here if needed
        }
        setModalState(false);
      } catch (error) {
        console.error("Error during form submission:", error);
        Notify("error", "Failed to update channel");
      }
    },
  });

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "number",
      header: "No.",
      size: 70,
      Cell: ({ row }) => (
        <Typography>{(page - 1) * rowsPerPage + row.index + 1}</Typography>
      ),
    },
    {
      accessorKey: "avatar_img",
      header: "Cover Image",
      size: 150,
      Cell: ({ row }: any) => {
        const imgSrc = row.original.tc_logo != null ? 'http://midas.mtrcb.gov.ph/etransact/assets/uploads/company_logo/' + row.original.tc_logo : 'http://midas.mtrcb.gov.ph/etransact/assets/uploads/pm/pm.png';
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <img
              src={imgSrc}
              style={{ height: 40, cursor: "pointer", objectFit: "contain" }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(imgSrc, "_blank");
              }}
              alt="Cover Image"
            />
          </Box>
        );
      },
    },
    {
      accessorKey: "tc_name",
      header: "Title",
      size: 300,
    },
    {
      accessorKey: "is_app_displayed",
      header: "Status",
      size: 150,
      Cell: ({ row }: any) => {
        const isActive = Boolean(row.original.is_app_displayed);
        return <StatusChip label={isActive ? "ACTIVE" : "INACTIVE"} />;
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 100,
      Cell: ({ row }: any) => (
        <RowActions
          actions={[
            {
              label: "Edit",
              onClick: () => openAddEditModal(row.original),
            },
          ]}
        />
      ),
    },
  ];

  const navigate = useNavigate();

  const filteredData = useMemo(() => {
    if (globalFilter) {
      return channels?.filter((channel: any) =>
        channel.tc_name?.toLowerCase().includes(globalFilter.toLowerCase())
      );
    }
    return channels ?? [];
  }, [globalFilter, channels]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  const handleSearch = () => {
    setGlobalFilter(draftSearch);
    setPage(1);
  };

  const handleClear = () => {
    setDraftSearch("");
    setGlobalFilter("");
    setPage(1);
  };

  return (
    <>
      <DefaultPage
        title="Channels"
        subTitle="Manage and review TV channel configurations for the App."
        loading={isLoading}
      >
        <Box mb={3}>
          <Alert severity="info">
            Displays TV channels available in the system. Set the filters and click <strong>Search</strong> to load results.
          </Alert>
        </Box>

        {isError && (
          <Box mb={3}>
            <Alert severity="error">
              Failed to load TV channels.
            </Alert>
          </Box>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <Box display="flex" flexWrap="wrap" gap={2}>
            <TextField
              size="small"
              label="Search Channel Title"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              sx={{ minWidth: 260 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </Box>

          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isFetching}
            >
              {isFetching ? "Searching..." : "Search"}
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClear}
              disabled={isFetching}
            >
              Clear
            </Button>
          </Box>
        </Box>

        <Box mb={2}>
          <Alert severity="success">
            Total matched records:{" "}
            <strong>{Number(filteredData?.length || 0).toLocaleString()}</strong>
          </Alert>
        </Box>

        <DataTableFive<any>
          data={paginatedData}
          total={filteredData?.length || 0}
          page={page}
          pageSize={rowsPerPage}
          onPageChange={(newPage) => {
            setPage(Math.max(newPage, 1));
          }}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={isLoading}
          isFetching={isFetching}
          onRowClick={(row: any) => openAddEditModal(row.original)}
        />

        <Dialog
          open={modalState}
          onClose={handleCloseModal}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "6px",
              border: "1px solid #E2E8F0",
              p: 2.5,
              backgroundColor: "#FFFFFF",
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0F172A" }}>
                {editMode ? "Edit Channel Configuration" : "Add Channel"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.25 }}>
                {selectedItem?.tc_name || "Manage channel visibility for the mobile app."}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseModal}
              sx={{ color: "#94A3B8", "&:hover": { color: "#475569" } }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ my: 1.5, borderColor: "#F1F5F9" }} />

          <form noValidate onSubmit={formik.handleSubmit}>
            <Box sx={{ py: 1 }}>
              <Typography sx={{ fontSize: 12.5, color: "#64748B", mb: 1.5 }}>
                Configure whether this channel is actively displayed and accessible to users on the MTRCB Mobile App.
              </Typography>

              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_app_displayed"
                      checked={formik.values.is_app_displayed}
                      onChange={formik.handleChange}
                      sx={{
                        color: "#CBD5E1",
                        "&.Mui-checked": { color: "#0072BC" },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}>
                        Display in Mobile App (Active)
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        {formik.values.is_app_displayed
                          ? "This channel is currently visible in the TV channels directory."
                          : "This channel is hidden from mobile app users."}
                      </Typography>
                    </Box>
                  }
                />
              </FormControl>
            </Box>

            <Divider sx={{ my: 2, borderColor: "#F1F5F9" }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCloseModal}
                disabled={updateChannelLoading || formik.isSubmitting}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  borderColor: "#CBD5E1",
                  color: "#64748B",
                  "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
                }}
              >
                Cancel
              </Button>

              <LoadingButton
                type="submit"
                variant="contained"
                size="small"
                loading={updateChannelLoading || formik.isSubmitting}
                sx={{
                  backgroundColor: "#0072BC",
                  "&:hover": { backgroundColor: "#005a96" },
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 650,
                  fontSize: 13,
                  px: 2.5,
                  boxShadow: "none",
                }}
              >
                Save Changes
              </LoadingButton>
            </Box>
          </form>
        </Dialog>
      </DefaultPage>
    </>
  );
}
